import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import api, { formatApiErrorDetail } from "@/lib/api";

const PORTAL_2DP_KEYS = ["product::长", "product::宽", "product::高", "product::毛重"];
const EDITABLE_PRICE_KEY = "product::Price";

function VariantPriceCell({ row, layout, recordId, onSaved }) {
  const original = row[EDITABLE_PRICE_KEY];
  const originalStr = original === "" || original == null ? "" : String(Number(original));
  const [value, setValue] = useState(originalStr);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const dirty = value.trim() !== originalStr && value.trim() !== "";

  const handleSave = async () => {
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      toast({ title: "Invalid price", description: "Enter a non-negative number.", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      await api.patch(
        `/fm/records/${recordId}/variants/${row.recordId}/price?layout=${encodeURIComponent(layout)}`,
        { price: num }
      );
      toast({ title: "Variant price updated" });
      onSaved?.();
    } catch (e) {
      toast({
        title: "Failed to update price",
        description: formatApiErrorDetail(e.response?.data?.detail),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex items-center justify-end gap-1.5">
      <Input
        type="number"
        step="0.01"
        min="0"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && dirty && !saving) handleSave();
        }}
        className="h-7 w-20 rounded-sm border-zinc-300 px-1.5 text-right text-sm font-mono-data"
        data-testid={`variant-price-input-${row.recordId}`}
      />
      {dirty && (
        <Button
          size="sm"
          variant="outline"
          className="h-7 rounded-sm border-zinc-300 px-2 text-xs"
          disabled={saving}
          onClick={handleSave}
          data-testid={`variant-price-save-${row.recordId}`}
        >
          {saving ? "..." : "Save"}
        </Button>
      )}
    </div>
  );
}

export function DrawerVariantsTable({ portalRows, portalColumns, layout, recordId, canEditVariantPrice = false, onVariantSaved }) {
  if (portalRows.length === 0) return null;

  return (
    <div>
      <p className="mb-2 text-xs uppercase tracking-[0.05em] text-zinc-500">Product Variants</p>
      <div className="overflow-x-auto rounded-sm border border-zinc-300">
        <table className="w-full text-sm" data-testid="product-portal-table">
          <thead className="bg-zinc-50">
            <tr className="border-b border-zinc-300">
              {portalColumns.map((col) => (
                <th
                  key={col.key}
                  className="px-2 py-1.5 text-right text-xs uppercase tracking-[0.05em] text-zinc-500 first:text-left"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {portalRows.map((row) => (
              <tr
                key={row.recordId}
                className="border-b border-zinc-200 last:border-0"
                data-testid={`product-portal-row-${row.recordId}`}
              >
                {portalColumns.map((col) => {
                  const raw = row[col.key];
                  const hasValue = raw !== null && raw !== undefined && raw !== "";
                  const isEditablePrice = canEditVariantPrice && col.key === EDITABLE_PRICE_KEY && recordId;
                  const display =
                    col.key === "product::inventory qty" && hasValue
                      ? Math.round(Number(raw))
                      : PORTAL_2DP_KEYS.includes(col.key) && hasValue
                      ? Number(raw).toFixed(2)
                      : raw;
                  return (
                    <td
                      key={col.key}
                      className="px-2 py-1.5 text-right font-mono-data text-zinc-800 first:text-left first:font-sans"
                      data-testid={col.key === "barcode" ? `product-portal-barcode-${row.recordId}` : undefined}
                    >
                      {isEditablePrice ? (
                        <VariantPriceCell
                          row={row}
                          layout={layout}
                          recordId={recordId}
                          onSaved={onVariantSaved}
                        />
                      ) : (
                        String(display ?? "-").replace(/\r/g, " ") || "-"
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
